import { useState, useRef, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, RotateCw, Check, Move, Maximize2, Square, RectangleHorizontal, RectangleVertical } from 'lucide-react';
import { Button } from './ui/button';

// Aspect ratio presets
const ASPECT_RATIOS = [
  { id: 'free', name: 'Free', icon: Maximize2, ratio: null },
  { id: 'square', name: 'Square', icon: Square, ratio: 1 },
  { id: 'landscape', name: 'Landscape', icon: RectangleHorizontal, ratio: 4/3 },
  { id: 'portrait', name: 'Portrait', icon: RectangleVertical, ratio: 3/4 },
  { id: 'wide', name: 'Wide', icon: RectangleHorizontal, ratio: 16/9 },
  { id: 'tall', name: 'Tall', icon: RectangleVertical, ratio: 9/16 },
];

export const ImageCropper = ({ imageUrl, itemTitle, onSave, onCancel, initialAspectRatio = 'free', detectionBox = null }) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // Rotation in degrees
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio);
  const [cropBox, setCropBox] = useState({ width: 100, height: 100 }); // percentages
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [initialized, setInitialized] = useState(false);

  // Initialize zoom and position based on detection box when image loads
  useEffect(() => {
    if (imageLoaded && !initialized && containerRef.current && imageRef.current && detectionBox) {
      const container = containerRef.current.getBoundingClientRect();
      const img = imageRef.current;
      
      // Detection box is in percentages of the original image
      const { x: boxX, y: boxY, width: boxWidth, height: boxHeight } = detectionBox;
      
      // Calculate what zoom level we need to make the detection box fill most of the view
      // We want the detection box to occupy about 80% of the container
      const targetFillPercent = 0.8;
      
      const zoomForWidth = (container.width * targetFillPercent) / ((boxWidth / 100) * img.offsetWidth);
      const zoomForHeight = (container.height * targetFillPercent) / ((boxHeight / 100) * img.offsetHeight);
      const idealZoom = Math.min(zoomForWidth, zoomForHeight, 5); // Cap at 5x
      
      setZoom(Math.max(1, idealZoom));
      
      // Calculate position to center the detection box in the container
      // The center of the detection box in image coordinates (as percentage)
      const boxCenterX = boxX + boxWidth / 2;
      const boxCenterY = boxY + boxHeight / 2;
      
      // We need to offset the image so this center point is in the middle of the container
      // This needs to account for the zoom level
      setTimeout(() => {
        if (imageRef.current && containerRef.current) {
          const imgRect = imageRef.current.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();
          
          // Where the box center currently is (in container coordinates)
          const currentCenterX = (boxCenterX / 100) * imgRect.width;
          const currentCenterY = (boxCenterY / 100) * imgRect.height;
          
          // Where we want it to be (center of container)
          const targetX = containerRect.width / 2;
          const targetY = containerRect.height / 2;
          
          // Calculate the offset needed
          const offsetX = targetX - currentCenterX;
          const offsetY = targetY - currentCenterY;
          
          setPosition({ x: offsetX, y: offsetY });
        }
      }, 50);
      
      // Set crop box to roughly match detection box proportions
      setCropBox({ 
        width: Math.min(85, Math.max(20, boxWidth * 1.1)), 
        height: Math.min(85, Math.max(20, boxHeight * 1.1)) 
      });
      
      setInitialized(true);
    } else if (imageLoaded && !initialized && !detectionBox) {
      // No detection box - use default view
      setInitialized(true);
    }
  }, [imageLoaded, initialized, detectionBox]);

  // Calculate crop box dimensions based on aspect ratio (only if not using detection box)
  useEffect(() => {
    if (!initialized) return; // Wait for detection box initialization first
    
    const ratio = ASPECT_RATIOS.find(r => r.id === aspectRatio)?.ratio;
    if (ratio && containerRef.current) {
      const container = containerRef.current.getBoundingClientRect();
      const containerRatio = container.width / container.height;
      
      if (ratio > containerRatio) {
        // Width limited
        setCropBox({ width: 90, height: (90 / ratio) * containerRatio });
      } else {
        // Height limited
        setCropBox({ width: 90 * ratio / containerRatio, height: 90 });
      }
    } else if (!ratio && initialized && !detectionBox) {
      // Free form without detection box - use default 80% square
      setCropBox({ width: 80, height: 80 });
    }
  }, [aspectRatio, initialized, detectionBox]);

  // Reset to default view
  const handleReset = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setRotation(0);
  };

  // Rotation controls with hard limits (-180 to 180)
  const clampRotation = (value) => Math.max(-180, Math.min(180, value));
  const handleRotateLeft = () => setRotation(prev => clampRotation(prev - 90));
  const handleRotateRight = () => setRotation(prev => clampRotation(prev + 90));
  const handleRotateFine = (degrees) => setRotation(prev => clampRotation(prev + degrees));
  const handleRotationSlider = (value) => setRotation(clampRotation(value));

  // Zoom controls - increased max to 10x (1000%)
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 10));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.5, 0.5));

  // Mouse/Touch handlers for panning
  const handleMouseDown = (e) => {
    if (e.target.closest('.resize-handle')) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (isResizing && containerRef.current) {
      const container = containerRef.current.getBoundingClientRect();
      const deltaX = ((e.clientX - resizeStart.x) / container.width) * 100;
      const deltaY = ((e.clientY - resizeStart.y) / container.height) * 100;
      
      const ratio = ASPECT_RATIOS.find(r => r.id === aspectRatio)?.ratio;
      
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      
      // Minimum 10% (reduced from 20% for skinnier crops)
      if (resizeHandle.includes('e')) newWidth = Math.max(10, Math.min(95, resizeStart.width + deltaX));
      if (resizeHandle.includes('w')) newWidth = Math.max(10, Math.min(95, resizeStart.width - deltaX));
      if (resizeHandle.includes('s')) newHeight = Math.max(10, Math.min(95, resizeStart.height + deltaY));
      if (resizeHandle.includes('n')) newHeight = Math.max(10, Math.min(95, resizeStart.height - deltaY));
      
      // Maintain aspect ratio if set
      if (ratio) {
        const containerRatio = container.width / container.height;
        if (resizeHandle.includes('e') || resizeHandle.includes('w')) {
          newHeight = (newWidth / ratio) * containerRatio;
        } else {
          newWidth = newHeight * ratio / containerRatio;
        }
      }
      
      setCropBox({ width: newWidth, height: newHeight });
      return;
    }
    
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart, isResizing, resizeHandle, resizeStart, aspectRatio]);

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    if (e.target.closest('.resize-handle')) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  // Resize handlers
  const handleResizeStart = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: cropBox.width,
      height: cropBox.height
    });
  };

  // Global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  // Save the cropped image
  const handleSave = async () => {
    if (!containerRef.current || !imageRef.current) return;
    
    setSaving(true);
    
    try {
      const container = containerRef.current.getBoundingClientRect();
      const img = imageRef.current;
      
      // Load original image for high quality crop
      const originalImg = new Image();
      originalImg.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        originalImg.onload = resolve;
        originalImg.onerror = reject;
        originalImg.src = imageUrl;
      });

      // Calculate the crop box dimensions in container
      const cropWidth = (cropBox.width / 100) * container.width;
      const cropHeight = (cropBox.height / 100) * container.height;
      
      // Create canvas with output dimensions (maintain aspect ratio, max 800px)
      const maxSize = 800;
      const outputRatio = cropWidth / cropHeight;
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (outputRatio > 1) {
        canvas.width = Math.min(maxSize, cropWidth * 2);
        canvas.height = canvas.width / outputRatio;
      } else {
        canvas.height = Math.min(maxSize, cropHeight * 2);
        canvas.width = canvas.height * outputRatio;
      }
      
      // Clear canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate scale factors
      const scaleX = originalImg.width / img.offsetWidth;
      const scaleY = originalImg.height / img.offsetHeight;
      
      // Calculate the visible area in original image coordinates
      const cropCenterX = container.width / 2;
      const cropCenterY = container.height / 2;
      
      // Where is the image center relative to the crop center?
      const imgCenterOffsetX = -position.x;
      const imgCenterOffsetY = -position.y;
      
      // Source dimensions in original image space
      const srcWidth = (cropWidth / zoom) * scaleX;
      const srcHeight = (cropHeight / zoom) * scaleY;
      
      // Source position (center of what we're cropping in original image)
      const srcCenterX = (originalImg.width / 2) + (imgCenterOffsetX / zoom) * scaleX;
      const srcCenterY = (originalImg.height / 2) + (imgCenterOffsetY / zoom) * scaleY;
      
      // Save context state
      ctx.save();
      
      // Move to canvas center, rotate, then draw
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      
      // Calculate source rectangle
      const srcX = srcCenterX - srcWidth / 2;
      const srcY = srcCenterY - srcHeight / 2;
      
      // Draw the image centered and rotated
      ctx.drawImage(
        originalImg,
        Math.max(0, srcX), 
        Math.max(0, srcY), 
        Math.min(srcWidth, originalImg.width - Math.max(0, srcX)), 
        Math.min(srcHeight, originalImg.height - Math.max(0, srcY)),
        -canvas.width / 2, 
        -canvas.height / 2, 
        canvas.width, 
        canvas.height
      );
      
      // Restore context state
      ctx.restore();
      
      // Convert to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      
      onSave(blob);
    } catch (error) {
      console.error('Failed to crop image:', error);
    } finally {
      setSaving(false);
    }
  };

  const currentRatio = ASPECT_RATIOS.find(r => r.id === aspectRatio);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white dark:bg-[#1a1a2e] rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E5DDD3] dark:border-gray-700">
          <div className="flex-1">
            <h3 className="font-bold text-[#1E3A5F] dark:text-white">Adjust Crop</h3>
            {itemTitle && (
              <p className="text-sm text-[#6B7280] mt-1 truncate">{itemTitle}</p>
            )}
          </div>
          <button 
            onClick={onCancel}
            className="w-8 h-8 rounded-full hover:bg-[#F5F0E8] dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-[#6B7280]" />
          </button>
        </div>
        
        {/* Aspect Ratio Selector */}
        <div className="p-3 border-b border-[#E5DDD3] dark:border-gray-700 bg-[#F8F5F0] dark:bg-[#1a1a2e]">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-xs text-[#6B7280] whitespace-nowrap">Aspect:</span>
            {ASPECT_RATIOS.map((ratio) => {
              const Icon = ratio.icon;
              return (
                <button
                  key={ratio.id}
                  onClick={() => setAspectRatio(ratio.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    aspectRatio === ratio.id
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-white dark:bg-gray-700 text-[#6B7280] hover:bg-[#E5DDD3] dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {ratio.name}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Crop Area */}
        <div className="p-4">
          <div 
            ref={containerRef}
            className="relative w-full bg-[#1E3A5F]/10 dark:bg-black/30 rounded-xl overflow-hidden cursor-move"
            style={{ height: '400px' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
          >
            {/* Image */}
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: isDragging ? 'none' : 'transform 0.15s ease-out'
              }}
            >
              <img 
                ref={imageRef}
                src={imageUrl} 
                alt="Crop preview"
                className="max-w-none select-none"
                style={{ 
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
                onLoad={() => setImageLoaded(true)}
                draggable={false}
              />
            </div>
            
            {/* Dark overlay outside crop area */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Top */}
              <div 
                className="absolute left-0 right-0 top-0 bg-black/50"
                style={{ height: `${(100 - cropBox.height) / 2}%` }}
              />
              {/* Bottom */}
              <div 
                className="absolute left-0 right-0 bottom-0 bg-black/50"
                style={{ height: `${(100 - cropBox.height) / 2}%` }}
              />
              {/* Left */}
              <div 
                className="absolute left-0 bg-black/50"
                style={{ 
                  top: `${(100 - cropBox.height) / 2}%`,
                  width: `${(100 - cropBox.width) / 2}%`,
                  height: `${cropBox.height}%`
                }}
              />
              {/* Right */}
              <div 
                className="absolute right-0 bg-black/50"
                style={{ 
                  top: `${(100 - cropBox.height) / 2}%`,
                  width: `${(100 - cropBox.width) / 2}%`,
                  height: `${cropBox.height}%`
                }}
              />
            </div>
            
            {/* Crop box with resize handles (only for free aspect ratio) */}
            <div 
              className="absolute border-2 border-white shadow-lg pointer-events-none"
              style={{
                left: `${(100 - cropBox.width) / 2}%`,
                top: `${(100 - cropBox.height) / 2}%`,
                width: `${cropBox.width}%`,
                height: `${cropBox.height}%`
              }}
            >
              {/* Grid lines */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="border border-white/30" />
                ))}
              </div>
              
              {/* Resize handles (only for free aspect) */}
              {aspectRatio === 'free' && (
                <>
                  {/* Corners */}
                  <div 
                    className="resize-handle absolute -top-2 -left-2 w-4 h-4 bg-white rounded-full cursor-nw-resize pointer-events-auto shadow-md"
                    onMouseDown={(e) => handleResizeStart(e, 'nw')}
                  />
                  <div 
                    className="resize-handle absolute -top-2 -right-2 w-4 h-4 bg-white rounded-full cursor-ne-resize pointer-events-auto shadow-md"
                    onMouseDown={(e) => handleResizeStart(e, 'ne')}
                  />
                  <div 
                    className="resize-handle absolute -bottom-2 -left-2 w-4 h-4 bg-white rounded-full cursor-sw-resize pointer-events-auto shadow-md"
                    onMouseDown={(e) => handleResizeStart(e, 'sw')}
                  />
                  <div 
                    className="resize-handle absolute -bottom-2 -right-2 w-4 h-4 bg-white rounded-full cursor-se-resize pointer-events-auto shadow-md"
                    onMouseDown={(e) => handleResizeStart(e, 'se')}
                  />
                  {/* Edges */}
                  <div 
                    className="resize-handle absolute top-1/2 -left-2 w-4 h-4 bg-white rounded-full cursor-w-resize pointer-events-auto shadow-md -translate-y-1/2"
                    onMouseDown={(e) => handleResizeStart(e, 'w')}
                  />
                  <div 
                    className="resize-handle absolute top-1/2 -right-2 w-4 h-4 bg-white rounded-full cursor-e-resize pointer-events-auto shadow-md -translate-y-1/2"
                    onMouseDown={(e) => handleResizeStart(e, 'e')}
                  />
                  <div 
                    className="resize-handle absolute -top-2 left-1/2 w-4 h-4 bg-white rounded-full cursor-n-resize pointer-events-auto shadow-md -translate-x-1/2"
                    onMouseDown={(e) => handleResizeStart(e, 'n')}
                  />
                  <div 
                    className="resize-handle absolute -bottom-2 left-1/2 w-4 h-4 bg-white rounded-full cursor-s-resize pointer-events-auto shadow-md -translate-x-1/2"
                    onMouseDown={(e) => handleResizeStart(e, 's')}
                  />
                </>
              )}
            </div>
            
            {/* Drag hint */}
            {imageLoaded && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/50 text-white text-xs">
                <Move className="w-3 h-3" />
                Drag image to pan
              </div>
            )}
          </div>
          
          {/* Controls Row */}
          <div className="flex items-center justify-center gap-6 mt-4">
            {/* Rotation Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleRotateLeft}
                className="w-10 h-10 rounded-full bg-[#F5F0E8] dark:bg-gray-700 hover:bg-[#E5DDD3] dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
                title="Rotate left 90°"
              >
                <RotateCcw className="w-5 h-5 text-[#1E3A5F] dark:text-white" />
              </button>
              
              <div className="flex items-center gap-1 min-w-[70px] justify-center">
                <span className="text-sm font-medium text-[#1E3A5F] dark:text-white">{rotation}°</span>
              </div>
              
              <button
                onClick={handleRotateRight}
                className="w-10 h-10 rounded-full bg-[#F5F0E8] dark:bg-gray-700 hover:bg-[#E5DDD3] dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
                title="Rotate right 90°"
              >
                <RotateCw className="w-5 h-5 text-[#1E3A5F] dark:text-white" />
              </button>
            </div>
            
            {/* Divider */}
            <div className="h-8 w-px bg-[#E5DDD3] dark:bg-gray-600" />
            
            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                className="w-10 h-10 rounded-full bg-[#F5F0E8] dark:bg-gray-700 hover:bg-[#E5DDD3] dark:hover:bg-gray-600 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Zoom out"
              >
                <ZoomOut className="w-5 h-5 text-[#1E3A5F] dark:text-white" />
              </button>
              
              <div className="flex items-center gap-1 min-w-[70px] justify-center">
                <span className="text-sm font-medium text-[#1E3A5F] dark:text-white">{Math.round(zoom * 100)}%</span>
              </div>
              
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 10}
                className="w-10 h-10 rounded-full bg-[#F5F0E8] dark:bg-gray-700 hover:bg-[#E5DDD3] dark:hover:bg-gray-600 flex items-center justify-center transition-colors disabled:opacity-50"
                title="Zoom in"
              >
                <ZoomIn className="w-5 h-5 text-[#1E3A5F] dark:text-white" />
              </button>
            </div>
            
            {/* Divider */}
            <div className="h-8 w-px bg-[#E5DDD3] dark:bg-gray-600" />
            
            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="w-10 h-10 rounded-full bg-[#F5F0E8] dark:bg-gray-700 hover:bg-[#E5DDD3] dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
              title="Reset all"
            >
              <X className="w-5 h-5 text-[#1E3A5F] dark:text-white" />
            </button>
          </div>
          
          {/* Sliders Section */}
          <div className="mt-4 px-4 space-y-3">
            {/* Rotation Slider */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#6B7280]">Rotation</span>
                <div className="flex gap-1">
                  <button 
                    onClick={() => handleRotateFine(-1)}
                    className="px-2 py-0.5 text-xs bg-[#F5F0E8] dark:bg-gray-700 rounded hover:bg-[#E5DDD3]"
                  >
                    -1°
                  </button>
                  <button 
                    onClick={() => handleRotateFine(1)}
                    className="px-2 py-0.5 text-xs bg-[#F5F0E8] dark:bg-gray-700 rounded hover:bg-[#E5DDD3]"
                  >
                    +1°
                  </button>
                </div>
              </div>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotation}
                onChange={(e) => handleRotationSlider(parseInt(e.target.value))}
                className="w-full h-2 bg-[#E5DDD3] dark:bg-gray-700 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none 
                  [&::-webkit-slider-thumb]:w-4 
                  [&::-webkit-slider-thumb]:h-4 
                  [&::-webkit-slider-thumb]:bg-[#C9956C] 
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-[#6B7280] mt-1">
                <span>-180°</span>
                <span>0°</span>
                <span>+180°</span>
              </div>
            </div>
            
            {/* Zoom Slider */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#6B7280]">Zoom level</span>
                <span className="text-xs text-[#1E3A5F] dark:text-white font-medium">{Math.round(zoom * 100)}%</span>
              </div>
              <input
                type="range"
                min="50"
                max="1000"
                step="10"
                value={zoom * 100}
                onChange={(e) => setZoom(parseInt(e.target.value) / 100)}
                className="w-full h-2 bg-[#E5DDD3] dark:bg-gray-700 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none 
                  [&::-webkit-slider-thumb]:w-4 
                  [&::-webkit-slider-thumb]:h-4 
                  [&::-webkit-slider-thumb]:bg-[#1E3A5F] 
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-[#6B7280] mt-1">
                <span>50%</span>
                <span>500%</span>
                <span>1000%</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-[#E5DDD3] dark:border-gray-700 bg-[#F8F5F0] dark:bg-[#1a1a2e]">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 rounded-full border-[#E5DDD3] dark:border-gray-600"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 rounded-full bg-[#1E3A5F] hover:bg-[#2D4A6F]"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Apply Crop
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
